// src/app/core/interceptors/auth.interceptor.ts - Functional Interceptor for Angular 19

import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, BehaviorSubject, filter, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { TranslateService } from '@ngx-translate/core';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const notification = inject(NotificationService);
  const translate = inject(TranslateService);

  // Skip authentication for public endpoints
  if (isPublicEndpoint(req.url)) {
    return next(req);
  }

  // Add auth token to requests
  const token = authService.getToken();
  const authReq = token ? addToken(req, token) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isRefreshUrl(req.url)) {
        return handleTokenRefresh(req, next, authService, router, notification, translate);
      }
      return throwError(() => error);
    })
  );
};

function handleTokenRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  notification: NotificationService,
  translate: TranslateService
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = authService.getRefreshToken();
    if (!refreshToken) {
      return handleSessionExpired(authService, router, notification, translate);
    }

    return authService.refreshToken().pipe(
      switchMap(response => {
        isRefreshing = false;
        refreshTokenSubject.next(response.token);
        // Retry original request with new token
        return next(addToken(req, response.token));
      }),
      catchError(err => {
        isRefreshing = false;
        refreshTokenSubject.next(null);
        return handleSessionExpired(authService, router, notification, translate);
      })
    );
  } else {
    // Another request is already refreshing — wait for the new token, then retry
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next(addToken(req, token!)))
    );
  }
}

function handleSessionExpired(
  authService: AuthService,
  router: Router,
  notification: NotificationService,
  translate: TranslateService
) {
  authService.clearSession();
  const currentUrl = router.url;
  notification.warning(translate.instant('auth.session_expired'));
  router.navigate(['/login'], { queryParams: { returnUrl: currentUrl } });
  return throwError(() => new HttpErrorResponse({ status: 401 }));
}

function addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` }
  });
}

function isPublicEndpoint(url: string): boolean {
  const publicEndpoints = [
    '/api/auth/',
    '/api/tournaments/public/',
    '/api/desk/',
    '/ws-tournament',
    '/api/health',
    '/swagger-ui',
    '/v3/api-docs'
  ];

  return publicEndpoints.some(endpoint => url.includes(endpoint));
}

function isRefreshUrl(url: string): boolean {
  return url.includes('/auth/refresh');
}
